import { Spinner, Switch, Text, makeStyles, shorthands } from '@fluentui/react-components';
import React from 'react';

const useStyles = makeStyles({
  toggleBlock: { display: 'flex', flexDirection: 'column', ...shorthands.gap('4px') },
  switchRow: { display: 'flex', alignItems: 'center', ...shorthands.gap('8px') },
  switchLabel: { color: '#535353' },
  description: { fontSize: '14px', color: '#535353' },
});

export interface SettingToggleProps {
  label: React.ReactNode;
  description: React.ReactNode;
  checked: boolean;
  /** Whether this specific toggle is currently persisting a change. */
  saving: boolean;
  /** Whether the switch should be disabled (e.g. while any save is in flight). */
  disabled: boolean;
  onChange: (checked: boolean) => void;
  switchTestId: string;
  savingTestId: string;
}

/**
 * Shared settings toggle row: a labelled Fluent Switch with an inline
 * "Saving…" spinner and a description. Used by the Tenant Configuration and
 * Edit Policy pages to keep the toggle markup and styling consistent.
 */
export const SettingToggle: React.FC<SettingToggleProps> = ({
  label, description, checked, saving, disabled, onChange, switchTestId, savingTestId,
}) => {
  const styles = useStyles();
  return (
    <div className={styles.toggleBlock}>
      <Text>{label}</Text>
      <div className={styles.switchRow}>
        <Switch
          checked={checked}
          onChange={(_, data) => onChange(data.checked)}
          label={{ children: checked ? 'On' : 'Off', className: styles.switchLabel }}
          disabled={disabled}
          data-testid={switchTestId}
        />
        {saving && (
          <Spinner size="tiny" label="Saving…" labelPosition="after" data-testid={savingTestId} />
        )}
      </div>
      <Text className={styles.description}>{description}</Text>
    </div>
  );
};
